// src/pages/accident/uploads.tsx
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import Webcam from "react-webcam";
import "../../helper/camera/ClCamera.css";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import Header, { HeaderType } from "../../components/Header";

import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import PublishIcon from "@mui/icons-material/Publish";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadIcon from "@mui/icons-material/Upload";
import {
  GetCurrentForm,
  NavigateTo,
  SetLocation,
  SetNextLocation,
  SetPreviouslocation,
} from "../../helper/navigation";
import { environment } from "../../environment/environment";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { useAuth, getAccessToken } from "@/context/auth";

// Shared webcam helpers (same as singleUpload)
import {
  CONSTRAINTS_1080P,
  normalizeForUpload,
  takeStillPhotoFromTrack,
  scaleToMax1080,
  upgradeVideoTrackQuality,
} from "../../helper/camera/webcam-utils";

interface UploadsProps {
  style?: any;
  loading: boolean;
  offline: boolean;
}

type Stage = "idle" | "camera" | "preview";
type Mode = "capture" | "rehydrated";

const Uploads: React.FC<UploadsProps> = (props) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const { tenant } = useParams<{ tenant?: string }>();

  // ---- DO NOT TOUCH NAVIGATION HELPERS ----
  const go = (path: string | number) => {
    if (typeof path === "number") return navigate(path);
    // strip leading slashes and go relative from /:tenant/*
    navigate(`../${path.replace(/^\/+/, "")}`);
  };

  const [subHeading, setSubHeading] = useState<string>("");
  const [formDescritpion, setFormDescritpion] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [, setUploading] = useState(false);

  const [stage, setStage] = useState<Stage>("idle");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  const webcamRef = useRef<Webcam | null>(null);

  const filePickerRef = useRef<HTMLInputElement | null>(null);
  const cameraPickerRef = useRef<HTMLInputElement | null>(null);

  const isMobile = useMemo(
    () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    [],
  );
  const [useNativeCamera, setUseNativeCamera] = useState<boolean>(isMobile);

  const [sp] = useSearchParams();
  const node = sp.get("node") ?? "";
  const step = sp.get("step") ?? "0";

  const cameFromFinal =
    sp.get("from") === "final" ||
    sessionStorage.getItem("returnToFinal") === "1";

  // Tenant-safe "go back to final"
  const goToFinal = () => {
    const raw = sessionStorage.getItem("finalReturnUrl");
    if (raw) {
      // If raw already contains /:tenant prefix, we just navigate to it.
      // If it doesn't, we prefix tenant (if present).
      const cleaned = raw.startsWith("/") ? raw : `/${raw}`;
      const base = tenant ? `/${tenant}` : "";
      const target =
        tenant && !cleaned.startsWith(base) ? `${base}${cleaned}` : cleaned;

      navigate(target, { replace: true });
      return;
    }

    const base = tenant ? `/${tenant}` : "";
    navigate(`${base}/accidentSupportFinal`, { replace: true });
  };

  const showConfirmDialog = (title: string, message: string) =>
    confirm({ catchOnCancel: false, title, description: message });

  const goPreserveFromFinal = (path: string) => {
    // NavigateTo() may already contain querystring.
    // If user came from Final, ensure the next page also has from=final
    if (!cameFromFinal) {
      go(path);
      return;
    }

    const [p, q] = path.split("?");
    const qs = new URLSearchParams(q ?? "");
    qs.set("from", "final");

    go(`${p}?${qs.toString()}`);
  };

  useEffect(() => {
    SetLocation(Number(step) || 0);
  }, [step, cameFromFinal]);

  // required gate for this step
  const [isRequired, setIsRequired] = useState(false);
  const [evidenceKey, setEvidenceKey] = useState<string>("");
  const [hasUploaded, setHasUploaded] = useState(false);

  // cache key + cached image + mode
  const [uploadCacheKey, setUploadCacheKey] = useState("");
  const [cachedImage, setCachedImage] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("capture");

  useEffect(() => {
    const current = GetCurrentForm();
    const req = !!current?.required;

    const evKey = `flowEvidence:${step}:${node}`;
    const cacheKey = `flowUploadCache:${step}:${node}`;

    setIsRequired(req);
    setEvidenceKey(evKey);
    setUploadCacheKey(cacheKey);

    // hydrate evidence
    setHasUploaded(evKey ? localStorage.getItem(evKey) === "1" : false);

    // hydrate cached image
    const cached = localStorage.getItem(cacheKey);
    if (cached && cached.startsWith("data:image/")) {
      setCachedImage(cached);
      setMode("rehydrated");
      setStage("idle");
      setCapturedImage(null);
    } else {
      setCachedImage(null);
      setMode("capture");
    }
  }, [step, node]);

  /* ---------------- set heading once ---------------- */
  useEffect(() => {
    const current = GetCurrentForm();
    const formId = current?.id ?? null;
    if (formId) {
      localStorage.setItem("formTypeId", JSON.stringify(formId));
    }
    setSubHeading(current?.name || "");
    setFormDescritpion(current?.description || "");
  }, [node]);

  /* ---------------- native pickers ---------------- */
  const openDevicePicker = () => filePickerRef.current?.click();
  const openNativeCameraPicker = () => cameraPickerRef.current?.click();

  const onPickedFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.onerror = rej;
      r.readAsDataURL(f);
    });

    const scaled1080p = await scaleToMax1080(dataUrl);
    setCapturedImage(scaled1080p);
    setStage("preview");
    setMode("capture"); // user is actively capturing again
    e.target.value = "";
  };

  /* ---------------- desktop webcam events ---------------- */
  const handleUserMediaError = useCallback(() => {
    setUseNativeCamera(true);
    openNativeCameraPicker();
  }, []);

  const handleUserMediaOk = useCallback(() => {
    setUseNativeCamera(false);
    upgradeVideoTrackQuality(webcamRef);
  }, []);

  /* ---------------- actions ---------------- */
  const startCamera = () => {
    setUseNativeCamera(false);
    setStage("camera");
    setMode("capture");
  };

  const captureImage = async () => {
    if (isMobile || useNativeCamera || !webcamRef.current) {
      openNativeCameraPicker();
      return;
    }

    let dataUrl = await takeStillPhotoFromTrack(webcamRef);
    if (!dataUrl) {
      dataUrl = (webcamRef.current as any)?.getScreenshot({
        width: 1920,
        height: 1080,
      }) as string | undefined;
    }
    if (!dataUrl) {
      setUseNativeCamera(true);
      openNativeCameraPicker();
      return;
    }

    const scaled1080p = await scaleToMax1080(dataUrl);
    setCapturedImage(scaled1080p);
    setStage("preview");
  };

  const discardImage = () => {
    setCapturedImage(null);
    setStage("idle");
  };

  const markThisStepDone = () => {
    const s = Number(step ?? "0");
    localStorage.setItem(`flowStepDone:${s}:${node}`, "1");
  };

  const routeToNextForm = () => {
    localStorage.removeItem("linkedDocumentID");
    markThisStepDone();

    // IMPORTANT: ensure index is correct before advancing
    SetLocation(Number(step) || 0);

    SetNextLocation();

    const currentForm = GetCurrentForm();
    setSubHeading(currentForm?.name || "");
    setFormDescritpion(currentForm?.description || "");

    goPreserveFromFinal(NavigateTo());
  };

  // Single source of truth for "Next"
  const goNext = () => {
    if (isRequired && !hasUploaded && !cachedImage) {
      return;
    }

    if (cameFromFinal) {
      markThisStepDone();
      goToFinal();
      return;
    }

    routeToNextForm();
  };


  const cancel = (close: boolean = false) => {
    localStorage.removeItem("linkedDocumentID");
    SetPreviouslocation();
    const currentForm = GetCurrentForm();
    setSubHeading(currentForm?.name || "");
    setFormDescritpion(currentForm?.description || "");
    go(NavigateTo({ cancel: close }));
  };

  const uploadImage = async () => {
    if (!capturedImage) {
      showConfirmDialog("No image", "Please capture an image first.");
      return;
    }
    setLoading(true);

    if (props.offline) {
      const key = `digialert_pwa_${Math.random().toString(36).slice(2, 7)}`;
      localStorage.setItem(key, capturedImage);
      showConfirmDialog(
        "Image saved locally!",
        "It appears you are offline. We cached your image and will upload once a connection is detected.",
      );
      discardImage();
      setLoading(false);
      return;
    }

    try {
      const accidentId = JSON.parse(
        localStorage.getItem("accidentId") || "null",
      );

      const current = GetCurrentForm();
      const formId =
        current?.id ?? JSON.parse(localStorage.getItem("formTypeId") || "null");

      const { base64, type } = await normalizeForUpload(capturedImage);

      const body = {
        linkedId: accidentId,
        fileName: `digialert_pwa_${accidentId}.${type}`,
        file: base64,
        fileType: type,
        documentTypeId: "00000000-0000-0000-0000-000000000004",
        formId: formId,
      };

      const config = { headers: { Authorization: `Bearer ${token}` } };
      setUploading(true);

      const resp = await axios.post(
        `${environment.baseUrl}/document`,
        body,
        config,
      );
      checkUploadStatus(resp, capturedImage);
    } catch {
      setUploading(false);
      setLoading(false);
      showConfirmDialog(
        "Failure",
        "An exception occurred, please contact your administrator.",
      );
    }
  };

  const checkUploadStatus = (resp: any, uploadedDataUrl?: string) => {
    setUploading(false);

    if (resp?.status >= 200 && resp?.status < 300) {
      // evidence
      if (isRequired && evidenceKey) {
        localStorage.setItem(evidenceKey, "1");
        setHasUploaded(true);
      }

      // cache for rehydration (step/node scoped)
      if (uploadCacheKey && uploadedDataUrl) {
        localStorage.setItem(uploadCacheKey, uploadedDataUrl);
        setCachedImage(uploadedDataUrl);
        setMode("rehydrated");
      }

      setLoading(false);
      showConfirmDialog("Success!", "Image uploaded to Media Library.").then(
        () => {
          discardImage();
          if (cameFromFinal) {
            markThisStepDone();
            goToFinal();
            return;
          }
          routeToNextForm();
        },
      );
    } else {
      setLoading(false);
      showConfirmDialog(
        "Failed to upload image!",
        "We were unable to upload your image.",
      );
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <Header type={HeaderType.AccidentSupportAdd} />
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={1}>
              <Grid size={12 as any}>
                <Grid container justifyContent="center">
                  <Typography variant="body1">{subHeading ?? ""}</Typography>
                </Grid>
                <br />

                <Grid container justifyContent="center">
                  {formDescritpion && (
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: formDescritpion || "",
                      }}
                      sx={{
                        "& p": { mb: 1 },
                        "& h1, & h2, & h3": { mt: 2, mb: 1 },
                        "& ul, & ol": { pl: 3, mb: 1 },
                        "& a": { color: "secondary.main" },
                      }}
                    />
                  )}
                </Grid>

                <br />
              </Grid>

              <Grid size={12 as any}>
                <Grid container justifyContent="center">
                  {/* Rehydrated preview */}
                  {mode === "rehydrated" && cachedImage && (
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          width: "100%",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "grey.100",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          component="img"
                          src={cachedImage}
                          alt="uploaded"
                          sx={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                            objectFit: "contain",
                          }}
                        />
                      </Box>

                      <Grid container spacing={1} sx={{ mt: 2 }}>
                        <Grid size={6 as any}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            disabled={loading}
                            onClick={() => {
                              setMode("capture");
                              setStage("idle");
                            }}
                          >
                            Add / Replace
                          </Button>
                        </Grid>
                        <Grid size={6 as any}>
                          <Button
                            variant="contained"
                            color={cameFromFinal ? "primary" : "secondary"}
                            fullWidth
                            disabled={loading}
                            onClick={goNext}
                          >
                            {cameFromFinal ? "Done" : "Next"}
                          </Button>
                        </Grid>

                        {!cameFromFinal && (
                          <Button
                            onClick={() => cancel()}
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            disabled={loading}
                            size="large"
                          >
                            <strong>Back</strong>
                          </Button>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* Desktop live camera, only when stage === 'camera' */}
                  {mode !== "rehydrated" && !isMobile && stage === "camera" && (
                    <div className="imageStage">
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        mirrored={false}
                        playsInline
                        screenshotFormat="image/jpeg"
                        screenshotQuality={1}
                        videoConstraints={CONSTRAINTS_1080P}
                        onUserMedia={handleUserMediaOk}
                        onUserMediaError={handleUserMediaError}
                        className="cameraVideo"
                      />
                    </div>
                  )}

                  {/* Preview of selected/captured image */}
                  {mode !== "rehydrated" &&
                    stage === "preview" &&
                    capturedImage && (
                      <div className="imageStage">
                        <img
                          src={capturedImage}
                          alt="captured"
                          className="cameraPreview"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}

                  {/* Hidden native inputs (always mounted) */}
                  <input
                    ref={filePickerRef}
                    type="file"
                    accept="image/*"
                    multiple={false}
                    style={{ display: "none" }}
                    onChange={onPickedFile}
                  />
                  <input
                    ref={cameraPickerRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple={false}
                    style={{ display: "none" }}
                    onChange={onPickedFile}
                  />
                </Grid>
              </Grid>

              <Grid size={12 as any}>
                <Grid container justifyContent="center">
                  {/* Only show capture UI when not rehydrated */}
                  {mode !== "rehydrated" && stage === "idle" && (
                    <>
                      <Grid size={12 as any}>
                        <Button
                          variant="outlined"
                          color="primary"
                          className="captureButton"
                          onClick={openDevicePicker}
                          startIcon={<UploadIcon />}
                          fullWidth
                          size="large"
                        >
                          Upload from device
                        </Button>
                      </Grid>

                      <br />
                      <br />

                      <Grid size={12 as any}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          className="captureButton"
                          onClick={() =>
                            isMobile ? openNativeCameraPicker() : startCamera()
                          }
                          startIcon={<CameraAltIcon />}
                          fullWidth
                          size="large"
                        >
                          {isMobile ? "Open camera" : "Open camera (desktop)"}
                        </Button>
                      </Grid>

                      <Grid size={12 as any}>
                        <Grid container justifyContent="center">
                          <Stack spacing={2} sx={{ pt: 1, width: "100%" }}>
                            <Button
                              onClick={goNext}
                              variant="contained"
                              color={cameFromFinal ? "primary" : "secondary"}
                              fullWidth
                              disabled={
                                loading ||
                                (isRequired && !hasUploaded && !cachedImage)
                              }
                              size="large"
                            >
                              <strong>{cameFromFinal ? "Done" : "Next"}</strong>
                            </Button>

                            {isRequired && !hasUploaded && !cachedImage && (
                              <Typography color="error" variant="body2">
                                This step is required. Please capture or upload at least one photo to continue.
                              </Typography>
                            )}

                            {!cameFromFinal && (
                              <Button
                                onClick={() => cancel()}
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                disabled={loading}
                                size="large"
                              >
                                <strong>Back</strong>
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {mode !== "rehydrated" && stage === "camera" && (
                    <>
                      <Grid size={12 as any}>
                        <Button
                          variant="contained"
                          color="secondary"
                          className="captureButton"
                          onClick={captureImage}
                          startIcon={<CameraAltIcon />}
                          fullWidth
                          size="large"
                        >
                          Capture Image
                        </Button>
                      </Grid>

                      <br />
                      <br />

                      <Grid size={12 as any}>
                        <br />

                        {cameFromFinal ? (
                          <Button
                            type="button"
                            onClick={goToFinal}
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={loading}
                            size="large"
                          >
                            <strong>Done</strong>
                          </Button>
                        ) : (
                          <Button
                            onClick={() => cancel()}
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            disabled={loading}
                            size="large"
                          >
                            <strong>Back</strong>
                          </Button>
                        )}
                      </Grid>
                    </>
                  )}

                  {mode !== "rehydrated" && stage === "preview" && (
                    <Grid size={12 as any}>
                      <Button
                        variant="contained"
                        color="primary"
                        className="captureButton"
                        onClick={uploadImage}
                        disabled={loading}
                        startIcon={<PublishIcon />}
                        fullWidth
                        size="large"
                      >
                        Accept
                      </Button>

                      <br />

                      <Button
                        variant="contained"
                        color="secondary"
                        className="deleteButton"
                        onClick={discardImage}
                        startIcon={<DeleteIcon />}
                        fullWidth
                        disabled={loading}
                        size="large"
                        sx={{ mt: 1 }}
                      >
                        Recapture
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <BrandLoader open={loading} />
    </>
  );
};

export default Uploads;
